import * as React from 'react';
import {Button, InputNumber, Modal, Table} from 'antd';
import {ColumnProps} from 'antd/lib/table';

import ButtonBar from '../common/ButtonBar';
import locales from '../locales';
import {numberGroupFormatter, renderFixedPriceColumn} from '../utils/number-utils';
import {InventorySaleEntry, InventoryStockItem, STATE_TARGET_SOLD,} from '../api/dtos/dtos';
import {simpleColumn} from "../utils/table-utils";
import ButtonGroup from "antd/lib/button/button-group";
import {showDeleteConfirm} from '../utils/dialogs';
import {Link} from "react-router-dom";
import * as paths from "../paths";
import InventoryStockSelector from "../maintenance/InventoryStockSelector";

export interface InventorySaleEntryEditorProps {
  disabled?: boolean;
  entries: InventorySaleEntry[];
  onEntryDelete: (entry: InventorySaleEntry) => void;
  onEntryUpdate: (entry: InventorySaleEntry) => void;
  onEntriesToAdd: (entries: InventorySaleEntry[]) => void;
}

interface State {
  inventoryStockSelectorVisible: boolean;
  costEditVisible: boolean;
  discountEditVisible: boolean;
  discountPercent?: number;
  editingEntry?: InventorySaleEntry;
}

export default class InventorySaleEntryEditor extends React.Component<InventorySaleEntryEditorProps, State> {
  public state: State = {
    inventoryStockSelectorVisible: false,
    costEditVisible: false,
    discountEditVisible: false,
  };

  public render(): React.ReactNode {
    const {entries, disabled} = this.props;

    const columns: Array<ColumnProps<InventorySaleEntry>> = [
      simpleColumn(locales.item_number, this.renderInventoryNumber, 160),
      simpleColumn(locales.appellation, entry => entry.name || '-'),
      simpleColumn(locales.amount, entry => `${numberGroupFormatter(entry.amount)} ${locales.pcs}`, 130),
      simpleColumn(locales.unit_price, this.renderUnitPrize),
      simpleColumn(locales.discount, this.renderDiscount),
      simpleColumn(locales.total, this.renderTotal, 160),
    ];

    columns.push(...[
      {
        title: '',
        key: 'actions',
        width: 48,
        render: this.renderActionColumn
      },
    ]);

    return (
      <>
        <ButtonBar style={{marginBottom: 8}}>
          <Button size="small" type="primary" icon="plus" onClick={this.onAddItemButtonClick}
                  disabled={disabled}>
            {locales.add}
          </Button>
        </ButtonBar>

        <Modal
          title={locales.inventory_stock_items}
          visible={this.state.inventoryStockSelectorVisible}
          footer={null}
          onCancel={this.onInventoryStockSelectorCancel}
          width="auto"
          style={{margin: '0 24px'}}
          destroyOnClose={true}
        >
          <InventoryStockSelector
            onSelect={this.onInventoryStockSelected}
            forSale={true}
            showInfiniteStocks={false}
          />
        </Modal>

        <Modal
          title={locales.unit_price}
          visible={this.state.costEditVisible}
          onCancel={this.onCostEditCancel}
          cancelText={locales.cancel}
          okText={locales.save}
          onOk={this.onCostEditConfirm}
          destroyOnClose={true}
        >
          <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8}}>
            <InputNumber
              style={{width: '50%'}}
              min={0}
              precision={0}
              value={this.state.editingEntry?.cost}
              placeholder={locales.unit_price}
              formatter={numberGroupFormatter}
              onChange={this.onCostDraftChange}
            />
            <span style={{whiteSpace: 'nowrap'}}>{locales.app_currency}</span>
          </div>
        </Modal>


        <Modal
          title={locales.discount}
          visible={this.state.discountEditVisible}
          onCancel={this.onDiscountEditCancel}
          cancelText={locales.cancel}
          okText={locales.save}
          onOk={this.onDiscountEditConfirm}
          destroyOnClose={true}
        >
          <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8}}>
            <InputNumber
              style={{width: '50%'}}
              min={0}
              max={100}
              precision={0}
              value={this.state.editingEntry?.discount}
              placeholder={locales.discount}
              onChange={this.onDiscountDraftChange}
            />
            <span style={{whiteSpace: 'nowrap'}}>%</span>
          </div>
        </Modal>

        <Table
          style={{marginBottom: 16}}
          dataSource={entries}
          columns={columns}
          rowKey={this.getRowKey}
          bordered={true}
          size="small"
          pagination={false}
          scroll={{x: 840}}
        />
      </>
    );
  }

  private getRowKey = (_: InventorySaleEntry, index: number): string => {
    return index.toString();
  };

  private renderInventoryNumber = (entry: InventorySaleEntry): React.ReactNode => {
    if (entry.id && entry.inventoryStockId) {
      return <Link to={{
        pathname: paths.viewInventoryStockPath(entry.inventoryStockId),
        state: {
          stateFilter: {
            targetType: STATE_TARGET_SOLD,
          },
        },
      }}>
        {entry.inventoryNumber}
      </Link>;
    }
    return entry.inventoryNumber || '-';

  }

  private renderDiscount = (entry: InventorySaleEntry): React.ReactNode => {
    return (
      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8}}>

        {entry.discount ? `${entry.discount} %` : '-'}

        <ButtonGroup>
          <Button
            icon="edit"
            title={locales.edit}
            disabled={this.props.disabled}
            onClick={this.onDiscountEditOpen(entry)}
          />
        </ButtonGroup>
      </div>
    );
  }

  private renderUnitPrize = (entry: InventorySaleEntry): React.ReactNode => {
    const costWasEdited = entry.originalCost.toFixed() !== entry.cost.toFixed();
    return (
      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8}}>
        <div style={{display: 'flex', flexDirection: 'column'}}>
          {costWasEdited && (
            <span style={{textDecoration: 'line-through', fontSize: '0.8em', color: '#999'}}>
              {renderFixedPriceColumn(entry.originalCost)}
            </span>
          )}
          <span>{renderFixedPriceColumn(entry.cost)}</span>
        </div>

        <ButtonGroup>
          <Button
            icon="edit"
            title={locales.edit}
            disabled={this.props.disabled}
            onClick={this.onCostEditOpen(entry)}
          />
        </ButtonGroup>
      </div>
    );
  }

  private renderTotal = (entry: InventorySaleEntry): React.ReactNode => {
    const originalTotal = entry.originalCost * entry.amount;
    const currentTotal = (entry.cost * entry.amount * (1 - (entry.discount ?? 0) / 100));
    const costWasEdited = entry.cost.toFixed() !== entry.originalCost.toFixed();
    const discountWasEdited = entry.discount !== undefined && entry.discount !== 0;

    return (
      <div style={{display: 'flex', flexDirection: 'column'}}>
        {(costWasEdited || discountWasEdited) && (
          <span style={{textDecoration: 'line-through', fontSize: '0.8em', color: '#999'}}>
            {renderFixedPriceColumn(originalTotal)}
          </span>
        )}
        <span>{renderFixedPriceColumn(currentTotal)}</span>
      </div>
    );
  }

  private renderActionColumn = (entry: InventorySaleEntry): React.ReactNode => {
    return (
      <div className="action-column" style={{textAlign: 'right'}}>
        <ButtonGroup>
          <Button
            icon="delete"
            title={locales.delete}
            disabled={this.props.disabled}
            onClick={this.onDeleteEntryButtonClick(entry)}
          />
        </ButtonGroup>
      </div>
    );
  };


  private onAddItemButtonClick = () => {
    if (this.props.disabled) {
      return;
    }
    this.setState({inventoryStockSelectorVisible: true});
  };

  private onInventoryStockSelectorCancel = () => {
    this.setState({inventoryStockSelectorVisible: false});
  };

  private onInventoryStockSelected = (stockItems: InventoryStockItem[], _: any) => {
    const newEntries = stockItems.map(this.toInventorySaleEntry);

    this.setState({
        inventoryStockSelectorVisible: false,
      }, () => this.props.onEntriesToAdd(newEntries)
    );
  };

  private toInventorySaleEntry = (stockItem: InventoryStockItem): InventorySaleEntry => ({
    id: undefined,
    inventoryStockId: stockItem.id,
    inventoryNumber: stockItem.inventoryStock?.inventoryNumber ?? null,
    name: stockItem.inventoryStock?.name || '',
    amount: 1,
    cost: this.getInventoryStockEntryCost(stockItem),
    originalCost: this.getInventoryStockEntryCost(stockItem),
  });

  private getInventoryStockEntryCost = (stockItem: InventoryStockItem): number => {
    return stockItem.hufCost * (1 + stockItem.profitMargin / 100);
  }

  private onCostEditOpen = (entry: InventorySaleEntry) => () => {
    if (this.props.disabled) {
      return;
    }
    this.setState({
      costEditVisible: true,
      editingEntry: entry,
    });
  };

  private onCostEditCancel = () => {
    this.setState({
      costEditVisible: false,
      editingEntry: undefined,
    });
  };

  private onDiscountEditOpen = (entry: InventorySaleEntry) => () => {
    if (this.props.disabled) {
      return;
    }
    this.setState({
      discountEditVisible: true,
      editingEntry: entry,
    });
  };

  private onDiscountEditCancel = () => {
    this.setState({
      discountEditVisible: false,
      editingEntry: undefined,
    });
  };

  private onDiscountEditConfirm = () => {
    const {editingEntry} = this.state;
    if (!editingEntry) {
      return;
    }

    this.setState({editingEntry: undefined, discountEditVisible: false}, () => this.props.onEntryUpdate(editingEntry));
  };

  private onDiscountDraftChange = (value: number | undefined) => {
    this.setState((prevState) => {
      if (!prevState.editingEntry) {
        return null;
      }
      return {
        editingEntry: {
          ...prevState.editingEntry,
          discount: value ?? prevState.editingEntry.discount,
        },
      };
    });
  };

  private onCostDraftChange = (value: number | undefined) => {
    this.setState((prevState) => {
      if (!prevState.editingEntry) {
        return null;
      }
      return {
        editingEntry: {
          ...prevState.editingEntry,
          cost: value ?? prevState.editingEntry.cost,
        },
      };
    });
  };

  private onCostEditConfirm = () => {
    const {editingEntry} = this.state;
    if (!editingEntry) {
      return;
    }

    this.setState({editingEntry: undefined, costEditVisible: false}, () => this.props.onEntryUpdate(editingEntry));
  };

  private onDeleteEntryButtonClick = (entry: InventorySaleEntry) => () => {
    showDeleteConfirm(() => this.props.onEntryDelete(entry));
  };

}



// WEBPACK FOOTER //
// ./src/sales/SalesEntryEditor.tsx